import { authenticatedInstance } from '../index/index.service';
import type { SessionCashCountView, UpsertSessionCashCountRequest } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

const headers = (employeeSession: string) => ({ headers: { 'x-employee-session': employeeSession } });

class SessionCashCountService {
  async get(tenantId: string, sessionId: number, employeeSession: string): Promise<SessionCashCountView | null> {
    try {
      const response = await authenticatedInstance.get<SessionCashCountView | null>(
        `/sessions/tenant/${encodeURIComponent(tenantId)}/${sessionId}/cash-count`,
        headers(employeeSession),
      );
      return response.data || null;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading cash count'));
    }
  }

  // Summary: Full-replace upsert; the server computes the total and snapshots expected cash.
  async upsert(
    tenantId: string,
    sessionId: number,
    payload: UpsertSessionCashCountRequest,
    employeeSession: string,
  ): Promise<SessionCashCountView> {
    try {
      const response = await authenticatedInstance.put<SessionCashCountView>(
        `/sessions/tenant/${encodeURIComponent(tenantId)}/${sessionId}/cash-count`,
        payload,
        headers(employeeSession),
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed saving cash count'));
    }
  }

  // Summary: Queues the captured count receipt on a printer; the server bumps print_count.
  async print(
    tenantId: string,
    sessionId: number,
    printerId: number,
    printableImageDataUrl: string,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.post<void>(
        `/sessions/tenant/${encodeURIComponent(tenantId)}/${sessionId}/cash-count/print`,
        { printerId, printableImageDataUrl },
        headers(employeeSession),
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed printing cash count'));
    }
  }
}

export const sessionCashCountService = new SessionCashCountService();
